import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Confetti, ChartLineUp, PiggyBank, Sparkle } from "@phosphor-icons/react";

export default function Landing() {
  return (
    <div className="min-h-screen relative z-10">
      <header className="max-w-7xl mx-auto flex justify-between items-center px-6 py-6" data-testid="landing-header">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 bg-violet-300 border-2 border-black rounded-lg flex items-center justify-center brutal-shadow-sm">
            <span className="font-outfit font-black text-lg">C</span>
          </div>
          <span className="font-outfit font-black text-2xl tracking-tight">CampusCash</span>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/login" className="brutal-btn bg-white hover:bg-yellow-100 px-5 py-2" data-testid="landing-login-btn">
            Log in
          </Link>
          <Link to="/register" className="brutal-btn bg-violet-300 hover:bg-violet-400 px-5 py-2" data-testid="landing-signup-btn">
            Sign up
          </Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-10 pb-24 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 bg-yellow-200 border-2 border-black rounded-full px-3 py-1 brutal-shadow-sm mb-6">
            <Sparkle size={14} weight="fill" />
            <span className="text-xs font-bold uppercase tracking-widest">AI-powered budget coach</span>
          </div>
          <h1 className="font-outfit font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6">
            Budget like a<br />
            <span className="bg-violet-300 border-2 border-black px-3 inline-block brutal-shadow">smart student</span>.
          </h1>
          <p className="font-manrope text-lg text-black/70 max-w-lg mb-8">
            Track expenses, set budgets, and hit savings goals — in any currency. Built for the rhythm of college life.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register" className="brutal-btn bg-violet-300 hover:bg-violet-400 px-8 py-3 text-base" data-testid="hero-cta-btn">
              Start free
            </Link>
            <Link to="/login" className="brutal-btn bg-white hover:bg-yellow-100 px-8 py-3 text-base" data-testid="hero-login-btn">
              I have an account
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          className="relative"
        >
          <div className="absolute -top-4 -left-4 w-24 h-24 bg-yellow-200 border-2 border-black rounded-full brutal-shadow" />
          <div className="absolute -bottom-6 -right-4 w-28 h-28 bg-emerald-200 border-2 border-black rounded-full brutal-shadow" />

          <div className="brutal-card p-8 relative">
            <div className="text-xs font-bold uppercase tracking-widest text-black/50 mb-1">This month</div>
            <div className="font-outfit font-black text-5xl mb-6">$842.50</div>

            <div className="space-y-4">
              <FeatureRow color="bg-emerald-200" Icon={ChartLineUp} title="Income" value="+$1,200" />
              <FeatureRow color="bg-red-200" Icon={PiggyBank} title="Expenses" value="-$357.50" />
              <FeatureRow color="bg-violet-200" Icon={Confetti} title="Saved for spring break" value="$420 / $600" />
            </div>
          </div>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24 grid md:grid-cols-3 gap-6">
        <FeatureCard title="Track everything" body="Log income and expenses in seconds. Categorize with taste." bg="bg-emerald-200" />
        <FeatureCard title="Any currency" body="Studying abroad or getting paid in ₹, €, £? We convert on the fly." bg="bg-yellow-200" />
        <FeatureCard title="AI budget tips" body="Get short, honest advice tailored to your spending — no lectures." bg="bg-violet-200" />
      </section>
    </div>
  );
}

function FeatureRow({ Icon, title, value, color }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 ${color} border-2 border-black rounded-lg flex items-center justify-center brutal-shadow-sm`}>
        <Icon size={20} weight="bold" />
      </div>
      <div className="flex-1 font-manrope font-semibold">{title}</div>
      <div className="font-outfit font-black">{value}</div>
    </div>
  );
}

function FeatureCard({ title, body, bg }) {
  return (
    <div className={`${bg} brutal-border rounded-xl brutal-shadow p-6`}>
      <h3 className="font-outfit font-black text-2xl mb-2">{title}</h3>
      <p className="font-manrope text-black/70">{body}</p>
    </div>
  );
}
